import React from 'react';
import { ShieldCheckIcon, DatabaseIcon, ScaleIcon, ClockIcon, UsersIcon, LockIcon, UserCheckIcon, MailIcon } from 'lucide-react';
import { FadeIn } from '../components/FadeIn';
import { PageHero } from '../components/PageHero';
import { CTASection } from '../components/CTASection';
interface PrivacyPageProps {
  onNavigate: (page: string) => void;
}
export function PrivacyPage({
  onNavigate
}: PrivacyPageProps) {
  const laatstBijgewerkt = new Date().toLocaleDateString('nl-NL', {
    day: 'numeric',
    month: 'long',
    year: 'numeric'
  });
  const sections = [{
    icon: <DatabaseIcon className="w-6 h-6 text-gold" data-id="element-811" />,
    title: 'Welke gegevens wij verwerken',
    body: <>
          <p data-id="element-812">
            Om je goed te kunnen adviseren en bemiddelen verwerken wij
            persoonsgegevens die je zelf aan ons verstrekt. Afhankelijk van je
            situatie gaat het onder meer om:
          </p>
          <ul className="mt-3 space-y-1 list-disc list-inside" data-id="element-813">
            <li data-id="element-814">Naam, adres, woonplaats, geboortedatum en burgerlijke staat</li>
            <li data-id="element-815">Contactgegevens zoals telefoonnummer en e-mailadres</li>
            <li data-id="element-816">
              Financiële gegevens zoals inkomen, vermogen, schulden en
              betaalgegevens
            </li>
            <li data-id="element-817">
              Gegevens over je woning, hypotheek, verzekeringen en dienstverband
            </li>
            <li data-id="element-818">Kopie van je identiteitsbewijs (waar wettelijk verplicht)</li>
          </ul>
        </>
  }, {
    icon: <ScaleIcon className="w-6 h-6 text-gold" data-id="element-819" />,
    title: 'Waarvoor en op welke grondslag',
    body: <>
          <p data-id="element-820">
            Wij verwerken je gegevens uitsluitend voor duidelijke doelen, op
            basis van de grondslagen uit de Algemene verordening
            gegevensbescherming (AVG):
          </p>
          <ul className="mt-3 space-y-1 list-disc list-inside" data-id="element-821">
            <li data-id="element-822">
              Het uitvoeren van onze overeenkomst: advies en bemiddeling bij
              hypotheken en verzekeringen
            </li>
            <li data-id="element-823">
              Het voldoen aan een wettelijke verplichting, zoals de Wet ter
              voorkoming van witwassen (Wwft) en de zorgplicht uit de Wft
            </li>
            <li data-id="element-824">
              Ons gerechtvaardigd belang, bijvoorbeeld om contact met je op te
              nemen naar aanleiding van je vraag
            </li>
            <li data-id="element-825">Je toestemming, die je altijd weer kunt intrekken</li>
          </ul>
        </>
  }, {
    icon: <UsersIcon className="w-6 h-6 text-gold" data-id="element-826" />,
    title: 'Delen met derden',
    body: <>
          <p data-id="element-827">
            Wij delen je gegevens alleen met partijen die nodig zijn om je te
            adviseren en je aanvraag te regelen. Denk aan geldverstrekkers,
            verzekeraars, taxateurs en het HDN-netwerk voor het veilig
            uitwisselen van aanvragen. Met verwerkers sluiten wij
            verwerkersovereenkomsten. Wij verkopen je gegevens nooit aan derden.
          </p>
        </>
  }, {
    icon: <ClockIcon className="w-6 h-6 text-gold" data-id="element-828" />,
    title: 'Bewaartermijn',
    body: <>
          <p data-id="element-829">
            Wij bewaren je gegevens niet langer dan nodig is voor de doelen
            waarvoor ze zijn verzameld, of zolang de wet ons daartoe verplicht.
            Voor financiële dossiers geldt doorgaans een wettelijke
            bewaartermijn tot zeven jaar na afloop van de dienstverlening.
          </p>
        </>
  }, {
    icon: <LockIcon className="w-6 h-6 text-gold" data-id="element-830" />,
    title: 'Beveiliging',
    body: <>
          <p data-id="element-831">
            Wij nemen passende technische en organisatorische maatregelen om je
            gegevens te beschermen tegen verlies of onrechtmatig gebruik, zoals
            beveiligde verbindingen, toegangsbeperking en het uitwisselen van
            gevoelige documenten via beveiligde kanalen.
          </p>
        </>
  }, {
    icon: <UserCheckIcon className="w-6 h-6 text-gold" data-id="element-832" />,
    title: 'Jouw rechten',
    body: <>
          <p data-id="element-833">
            Je hebt het recht om je persoonsgegevens in te zien, te laten
            corrigeren of te laten verwijderen. Ook kun je bezwaar maken tegen
            de verwerking en je gegevens laten overdragen. Stuur je verzoek naar{' '}
            <a href="mailto:info@jdgadvies.nl" className="text-gold font-semibold hover:underline" data-id="element-834">
              info@jdgadvies.nl
            </a>
            . Kom je er met ons niet uit? Dan kun je een klacht indienen bij de
            Autoriteit Persoonsgegevens via autoriteitpersoonsgegevens.nl.
          </p>
        </>
  }];
  return <div className="w-full" data-id="element-835">
      <PageHero title="Privacyverklaring" subtitle="Wij gaan zorgvuldig en vertrouwelijk om met je persoonsgegevens. Hieronder lees je hoe wij dat doen." label="Privacy" data-id="element-836" />

      <section className="py-20 md:py-28 bg-white" data-id="element-837">
        <div className="max-w-3xl mx-auto px-4 md:px-8" data-id="element-838">
          <FadeIn data-id="element-839">
            <div className="flex items-center gap-3 mb-8 text-sm text-gray-500" data-id="element-840">
              <ShieldCheckIcon className="w-5 h-5 text-gold flex-shrink-0" data-id="element-841" />
              <span data-id="element-842">Laatst bijgewerkt op {laatstBijgewerkt}</span>
            </div>
            <div className="space-y-6 text-lg text-gray-600 leading-relaxed" data-id="element-843">
              <p data-id="element-844">
                JDG Hypotheken | Verzekeringen, gevestigd aan de Sloterweg 228,
                1171 CX Badhoevedorp, is verantwoordelijk voor de verwerking van
                persoonsgegevens zoals beschreven in deze privacyverklaring. Wij
                staan geregistreerd bij de AFM onder nummer 12051006 en bij de
                KVK onder nummer 42010917.
              </p>
            </div>
          </FadeIn>

          <div className="mt-12 space-y-8" data-id="element-845">
            {sections.map((section, index) => <FadeIn key={index} delay={index * 0.05} className="bg-warm rounded-2xl border border-gray-100 p-6 md:p-8" data-id="element-846">
                <div className="flex items-center gap-4 mb-4" data-id="element-847">
                  <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center flex-shrink-0 shadow-sm" data-id="element-848">
                    {section.icon}
                  </div>
                  <h2 className="text-xl md:text-2xl font-bold text-navy" data-id="element-849">
                    {section.title}
                  </h2>
                </div>
                <div className="text-gray-600 leading-relaxed" data-id="element-850">
                  {section.body}
                </div>
              </FadeIn>)}
          </div>

          <FadeIn delay={0.3} className="mt-12" data-id="element-851">
            <div className="bg-navy rounded-2xl p-8 text-white flex flex-col sm:flex-row items-start gap-5" data-id="element-852">
              <div className="w-12 h-12 bg-white/10 rounded-full flex items-center justify-center flex-shrink-0" data-id="element-853">
                <MailIcon className="w-6 h-6 text-gold" data-id="element-854" />
              </div>
              <div data-id="element-855">
                <h2 className="text-xl font-bold text-gold mb-2" data-id="element-856">
                  Vragen over je privacy?
                </h2>
                <p className="text-gray-300 leading-relaxed" data-id="element-857">
                  Neem gerust contact met ons op via{' '}
                  <a href="mailto:info@jdgadvies.nl" className="text-white font-semibold hover:underline" data-id="element-858">
                    info@jdgadvies.nl
                  </a>{' '}
                  of bel{' '}
                  <a href="tel:0232052161" className="text-white font-semibold hover:underline" data-id="element-859">
                    023-205 2161
                  </a>
                  .
                </p>
              </div>
            </div>
          </FadeIn>
        </div>
      </section>

      <CTASection title="Nog vragen? Wij helpen je graag" subtitle="Neem vrijblijvend contact op. Wij nemen de tijd om je situatie te begrijpen." buttonText="Neem contact op" onNavigate={onNavigate} page="contact" variant="dark" data-id="element-860" />
    </div>;
}